import { Api, CampaignSplitFilterGroupsForSplitResponse, CampaignSplitProduct, CampaignSplitProductRequest, CampaignSplitStatisticsResponse, CustomField, Filter, GetCampaignSplitStatisticsIdParams, GetCustomFieldParams, GetFilterParams, GetProductsParams, PosLight, ProductLight, ProductsResponse, ProductType } from '~/api';
import { buildModule, createFiltersModule, createPaginationModule, defineArrayFilter, defineSingleFilter } from '../utils';
import $axios from '~/plugins/axios';
import { abortableRequest, abortRequest, assertApiError, createDict, createIndex, Dict, fetchAllPages, ignoringAbort } from '~/helpers';

const GET_PRODUCTS_ABORT_KEY = 'campaignSplit/getProducts';
const GET_STATISTICS_ABORT_KEY = 'campaignSplit/getStatistics';
import detail from './detail';
import lazy from './lazy';
import list from './list';

export type ProductWithPos = Omit<ProductLight, 'pos'> & { pos: PosLight }
export type ProductWithPosAndProductType = ProductWithPos & { productType: ProductType };
type PosFilter = GetProductsParams['posFilter'];
type ProductTypeFilter = GetProductsParams['productTypeFilter'];

// TODO: docs not written per openapi spec, workaround to allow optional filters before docs and subsequent typegen fix
export type GetProductsParamsWithOptionalFilters = Omit<GetProductsParams, 'productTypeFilter' | 'posFilter'> & {
	productTypeFilter?: Partial<ProductTypeFilter> | null;
	posFilter?: Partial<PosFilter> | null;
};

export type GetCampaignSplitStatisticsIdParamsWithOptionalFilters = Omit<GetCampaignSplitStatisticsIdParams, 'productTypeFilter' | 'posFilter'> & {
	productTypeFilter?: Partial<ProductTypeFilter> | null;
	posFilter?: Partial<PosFilter> | null;
};

type CampaignSplitFilterGroupForSplit = CampaignSplitFilterGroupsForSplitResponse['payload'][number];

const api = $axios as Api;

export default buildModule()
	.withSubmodules({
		detail,
		lazy,
		list,
		posFilters: createFiltersModule({
			filters: {
				searchTerm: defineSingleFilter('posSearchTerm', String),
				customFilterIds: defineArrayFilter('posFilterId', Number),
				customFilterValues: defineArrayFilter('posFilterValue', String),
				posTypeIds: defineArrayFilter('posTypeIds', Number),
				areaId: defineArrayFilter('areaId', Number),
				regionId: defineArrayFilter('regionId', Number),
			},
		}),
		productTypeFilters: createFiltersModule({
			filters: {
				excludedSplitId: defineSingleFilter('excludedSplitId', Number),
				searchTerm: defineSingleFilter('searchTerm', String),
				posZoneId: defineArrayFilter('posZoneId', Number),
				productCategoryId: defineArrayFilter('productCategoryId', Number),
				productClassId: defineSingleFilter('productClassId', Number),
				customFilterIds: defineArrayFilter('productTypeFilterId', Number),
				customFilterValues: defineArrayFilter('productTypeFilterValue', String),
			},
		}),
		statisticsFilters: createFiltersModule({
			filters: {
				onlyPosesWithProducts: defineSingleFilter('onlyPosesWithProducts', Boolean),
				campaignSplitId: defineSingleFilter('campaignSplitId', Number),
				campaignCategoryIds: defineArrayFilter('campaignCategoryIds', Number),
				campaignTypeIds: defineArrayFilter('campaignTypeIds', Number),
			},
		}),
		filterGroupsPagination: createPaginationModule({
			pageQueryKey: 'filterPage',
			pageSizeQueryKey: 'filterPerPage',
			defaultPageSize: 10,
		}),
	})
	.withState({
		customPosFilters: [] as Filter[],
		customProductTypeFilters: [] as CustomField[],
		productPosesIds: [] as number[],
		storedProductPoses: {} as Dict<PosLight>,
		productsIds: [] as number[],
		storedProducts: {} as Dict<ProductWithPosAndProductType>,
		splits: [] as CampaignSplitProduct[],
		splitFilterGroupIds: [] as number[],
		storedSplitFilterGroups: {} as Dict<CampaignSplitFilterGroupForSplit>,
		statistics: {} as Dict<CampaignSplitStatisticsResponse['payload']>,
		pendingSearchRequestCount: 0,
	})
	.withGetters({
		customPosFilters: state => state.customPosFilters,
		customProductTypeFilters: state => state.customProductTypeFilters,
		products: state => state.productsIds.map(id => state.storedProducts[id]),
		productsById: state => (id: number): ProductWithPosAndProductType => state.storedProducts[id] ?? null,
		productPoses: state => state.productPosesIds.map(id => state.storedProductPoses[id]),
		productPosById: state => (id: number): PosLight => state.storedProductPoses[id],
		splits: state => state.splits,
		splitFilterGroups: state => state.splitFilterGroupIds.map(id => state.storedSplitFilterGroups[id]),
		splitFilterGroupById: state => (id: number): CampaignSplitFilterGroupForSplit | null => state.storedSplitFilterGroups[id] ?? null,
		statisticsById: state => (id: number) => state.statistics[id] ?? null,
		someSearchRequestsPending: state => state.pendingSearchRequestCount > 0,
	})
	.withGetters({
		productsByPosId: (_, getters) => createIndex(getters.products, (product) => product.pos.id),
	})
	.withMutations({
		SET_CUSTOM_POS_FILTERS (state, value: Filter[]) {
			state.customPosFilters = value;
		},
		SET_CUSTOM_PRODUCT_TYPE_FILTERS (state, value: CustomField[]) {
			state.customProductTypeFilters = value;
		},
		SET_STORED_PRODUCT_POSES (state, value: PosLight[]) {
			state.storedProductPoses = {
				...state.storedProductPoses,
				...createDict(value, (pos) => pos.id),
			};
		},
		SET_PRODUCT_POSES (state, posesIds: number[]) {
			state.productPosesIds = posesIds;
		},
		SET_STORED_PRODUCTS (state, value: ProductWithPosAndProductType[]) {
			state.storedProducts = {
				...state.storedProducts,
				...createDict(value, (product) => product.id),
			};
		},
		SET_PRODUCTS (state, productsIds: number[]) {
			state.productsIds = productsIds;
		},
		SET_SPLITS (state, value: CampaignSplitProduct[]) {
			state.splits = [ ...value ];
		},
		SET_STORED_SPLIT_FILTER_GROUPS (state, value: CampaignSplitFilterGroupForSplit[]) {
			state.storedSplitFilterGroups = {
				...state.storedSplitFilterGroups,
				...createDict(value, (group) => group.id),
			};
		},
		SET_SPLIT_FILTER_GROUPS (state, groupIds: number[]) {
			state.splitFilterGroupIds = groupIds;
		},
		SET_STATISTICS (state, { id, ...statistics }: { id: number } & CampaignSplitStatisticsResponse['payload']) {
			state.statistics = { ...state.statistics, [id]: statistics };
		},
		UPDATE_PENDING_SEARCH_REQUESTS (state, by: -1 | 1) {
			state.pendingSearchRequestCount += by;
		},
	})
	.withActions({
		async getCustomPosFilters ({ commit }) {
			const params: GetFilterParams = {
				key: 'campaign',
				hasPosInfo: true,
			};
			const resp = await api.get('/filter', { params });
			commit('SET_CUSTOM_POS_FILTERS', resp.data.payload);
		},
		async getCustomProductTypeFilters ({ commit }) {
			const params: GetCustomFieldParams = {
				entity: 'store.productType',
			};
			const resp = await api.get('/custom-field', { params });
			commit('SET_CUSTOM_PRODUCT_TYPE_FILTERS', resp.data.payload);
		},
		async getProducts ({ commit }, params: GetProductsParamsWithOptionalFilters) {
			commit('UPDATE_PENDING_SEARCH_REQUESTS', 1);
			try {
				return await ignoringAbort(async () => {
					const hasPos = (product: ProductLight): product is ProductWithPos => product.pos != null;
					const data = await abortableRequest(
						GET_PRODUCTS_ABORT_KEY,
						(signal) => fetchAllPages('/products', params as GetProductsParams, 200, signal)<ProductsResponse['payload'][number]>(api),
					);
					const productsWithType = data.flatMap(item => item.products.filter(hasPos).map(product => ({ ...product, productType: item.productType })));
					const poses = productsWithType.map(item => item.pos).filter(pos => pos != null) as PosLight[];
					commit('SET_STORED_PRODUCTS', productsWithType);
					commit('SET_PRODUCTS', productsWithType.map(product => product.id));
					commit('SET_STORED_PRODUCT_POSES', poses);
					commit('SET_PRODUCT_POSES', poses.map(pos => pos.id));
					return data;
				});
			}
			finally {
				commit('UPDATE_PENDING_SEARCH_REQUESTS', -1);
			}
		},
		cancelGetProducts ({ commit }) {
			abortRequest(GET_PRODUCTS_ABORT_KEY);
			commit('SET_PRODUCTS', []);
			commit('SET_PRODUCT_POSES', []);
		},
		async getSplitFilterGroups ({ commit }, splitId: number) {
			const { data } = await api.get(`/campaign-split/${ splitId }/filter-group`);
			commit('SET_STORED_SPLIT_FILTER_GROUPS', data.payload);
			commit('SET_SPLIT_FILTER_GROUPS', data.payload.map(group => group.id));
		},
		async getSplits ({ commit }, splitId: number) {
			try {
				const data = await fetchAllPages(`/campaign-split/${ splitId }/product`, {})<CampaignSplitProduct>(api);
				commit('SET_SPLITS', data);
			}
			catch (error) {
				assertApiError(error);
				commit('SET_SPLITS', []);
				if (error.response.data.code === 404) {
					return;
				}
				throw error;
			}
		},
		async saveSplits ({ commit }, { id, ...params }: { id: number } & CampaignSplitProductRequest) {
			const { data } = await api.post(`/campaign-split/${ id }/product`, params);
			commit('SET_SPLITS', data.payload);
		},
		async getStatistics ({ commit }, { id, ...params }: { id: number } & GetCampaignSplitStatisticsIdParamsWithOptionalFilters) {
			await ignoringAbort(async () => {
				const { data } = await abortableRequest(
					GET_STATISTICS_ABORT_KEY,
					(signal) => api.get(`/campaign-split-statistics/${ id }`, { params: params as GetCampaignSplitStatisticsIdParams, signal }),
				);
				commit('SET_STATISTICS', { id, ...data.payload });
			});
		},
	});
